import {
  Button,
  Card,
  Checkbox,
  ColorInput,
  Grid,
  Group,
  MultiSelect,
  NumberInput,
  SegmentedControl,
  Select,
  Tabs,
  Text,
  TextInput,
} from '@mantine/core';
import {
  IconChevronLeft,
  IconChevronRight,
  IconCopy,
  IconDeviceFloppy,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import ISO6391 from 'iso-639-1';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import getMergeTags from '../../constants/getMergeTags';
import useFetch from '../../helpers/useFetch';
import DatePickerControl from '../DatePicker';
import ErrorMessage from '../ErrorMessage';
import LockableButton from '../Lockable/LockableButton';
import ConfirmationModal from '../Modals/ConfirmationModal';
import SegmentSelector from '../Segments/SegmentSelector';
import TagsSelect from '../TagSelector';
import URLFilterConfig from '../UrlFilterConfig';
import StorySlideEditor from './StorySlideEditor';
import StorySlideList from './StorySlideList';

const tabs = ['slides', 'display', 'audience', 'settings'];

const StoryForm = ({ story: passedStory, deleteStory, saveStory, cancel, onSuccess, copyStory, pages }) => {
  const fetch = useFetch();
  const { accountId, domainId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const search = queryString.parse(location.search);
  const storyTab = search.storyTab || tabs[0];
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [story, setStory] = useState(passedStory);
  const [slides, setSlides] = useState(passedStory.slides || passedStory.storySlides || []);
  const [userError, setUserError] = useState();
  const [loadError, setLoadError] = useState();
  const [editingSlideIndex, setEditingSlideIndex] = useState(null);
  const [customFields, setCustomFields] = useState();

  const setTab = (storyTab) => {
    navigate(`${location.pathname}?${queryString.stringify({ ...search, storyTab })}`);
  };

  const create = (storyData) => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/stories`, {
      method: 'POST',
      body: JSON.stringify(storyData),
    })
      .then(({ story }) => onSuccess(story))
      .catch(setUserError);
  };

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/customFields`)
      .then(({ customFields }) => setCustomFields(customFields))
      .catch(setLoadError);
  }, [fetch, accountId, domainId]);

  const handleStateChange = (e) => {
    const { name, value } = e.target;
    setStory((prev) => ({ ...prev, [name]: value }));
  };

  const addSlide = () => {
    const newSlide = {
      position: slides.length,
      html: '',
      design: '',
      durationSeconds: null,
      editor: 'visual',
    };
    setSlides([...slides, newSlide]);
    setEditingSlideIndex(slides.length);
  };

  const deleteSlide = (index) => {
    const updated = slides.filter((_, i) => i !== index).map((s, i) => ({ ...s, position: i }));
    setSlides(updated);
  };

  const saveSlide = (slideData) => {
    const updated = [...slides];
    updated[editingSlideIndex] = { ...slideData, position: editingSlideIndex };
    setSlides(updated);
    setEditingSlideIndex(null);
  };

  const validateStory = () => {
    if (!story.name) return false;
    if (!story.segmentId) return false;
    if (!story.pageId) return false;
    return true;
  };

  const doSaveStory = () => {
    const storyData = {
      ...story,
      slides: slides.map((s, i) => ({ ...s, position: i })),
    };
    saveStory ? saveStory(storyData) : create(storyData);
  };

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (customFields === undefined) {
    return '';
  }

  const mergeTags = getMergeTags(customFields).filter(
    (el) =>
      ![
        'profileId',
        'unsubscribeLink',
        'subscribeLink',
        'viewInBrowserLink',
        'subscriptionManagementLink',
        'dynamicCoupons',
      ].includes(el.key) && !el.key.startsWith('surveyLink')
  );

  const storyTypeOptions = [
    { label: 'Show always', value: 'showAlways' },
    { label: 'Show until click', value: 'showUntilClick' },
  ];

  const endBehaviorOptions = [
    { label: 'Dismiss', value: 'dismiss' },
    { label: 'Loop', value: 'loop' },
    { label: 'Stay on last', value: 'stayOnLast' },
  ];

  const triggerOptions = [
    { label: 'Show immediately', value: 'immediately' },
    { label: 'On scrolling down N% of page', value: 'scrollPercentage' },
    { label: 'After N seconds on the page', value: 'delaySeconds' },
    { label: 'On leave intent', value: 'leaveIntent' },
    { label: 'Added to Wishlist', value: 'wishlistChanged' },
    { label: 'Added to Cart', value: 'cartChanged' },
  ];

  const pageOptions = (pages || []).map((p) => ({
    label: p.name,
    value: p.id.toString(),
  }));

  const codes = ISO6391.getLanguages(ISO6391.getAllCodes());
  const localesOptions = codes.map((locale) => ({
    label: locale.name,
    value: locale.code,
  }));

  let deleteButton;
  if (typeof deleteStory === 'function') {
    deleteButton = (
      <LockableButton restrictionKey="storiesDelete" color="red" onClick={() => setConfirmDeleteOpen(true)}>
        <IconTrash size={14} /> Delete
      </LockableButton>
    );
  }

  let copyButton;
  if (typeof copyStory === 'function') {
    copyButton = (
      <LockableButton restrictionKey="storiesCreate" color="cyan" onClick={copyStory}>
        <IconCopy size={14} /> Make a copy
      </LockableButton>
    );
  }

  const saveButton = (
    <LockableButton
      restrictionKey={`stories${story.id ? 'Update' : 'Create'}`}
      disabled={!validateStory()}
      variant="primary"
      onClick={() => doSaveStory()}
    >
      <IconDeviceFloppy size={14} /> Save
    </LockableButton>
  );

  const nextButton = (
    <Button
      onClick={() => {
        setTab(tabs[tabs.indexOf(storyTab) + 1]);
        window.scrollTo(0, 0);
      }}
    >
      Next <IconChevronRight size={14} />
    </Button>
  );

  let reentryConfig = '';
  if (story.type === 'showUntilClick') {
    reentryConfig = (
      <>
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" fw={500}>
              After click
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <SegmentedControl
              value={String(story.reentrable)}
              onChange={(value) => handleStateChange({ target: { name: 'reentrable', value: value === 'true' } })}
              data={[
                { label: 'Never show again to this user', value: 'false' },
                { label: 'Show again after cooldown', value: 'true' },
              ]}
            />
            {story.reentrable && (
              <Grid>
                <Grid.Col span={{ base: 12 }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <TextInput
                      size="sm"
                      name="reentryCooldownInDays"
                      type="number"
                      min="0"
                      step="1"
                      value={story.reentryCooldownInDays || ''}
                      onChange={(e) =>
                        handleStateChange({
                          target: { name: e.target.name, value: e.target.value === '' ? null : e.target.value },
                        })
                      }
                      rightSection={<Text size="xs">day(s)</Text>}
                      rightSectionWidth={50}
                    />
                    <TextInput
                      size="sm"
                      name="reentryCooldownInHours"
                      type="number"
                      min="0"
                      step="1"
                      value={story.reentryCooldownInHours || ''}
                      onChange={(e) =>
                        handleStateChange({
                          target: { name: e.target.name, value: e.target.value === '' ? null : e.target.value },
                        })
                      }
                      rightSection={<Text size="xs">hour(s) after last entry</Text>}
                      rightSectionWidth={130}
                    />
                  </div>
                </Grid.Col>
              </Grid>
            )}
          </Grid.Col>
        </Grid>
      </>
    );
  }

  return (
    <>
      <Tabs value={storyTab} onChange={setTab} mb="md" keepMounted>
        <Tabs.List>
          <Tabs.Tab value="slides">Slides</Tabs.Tab>
          <Tabs.Tab value="display">Display</Tabs.Tab>
          <Tabs.Tab value="audience">Audience</Tabs.Tab>
          <Tabs.Tab value="settings">Settings</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="slides" pt={0}>
          <Card withBorder>
            <Card.Section inheritPadding py="sm">
              <Grid mb="md">
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Text size="sm" fw={500}>
                    Name
                  </Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <TextInput
                    name="name"
                    type="text"
                    value={story.name}
                    placeholder="e.g. Summer Sale Story"
                    onChange={(e) => handleStateChange(e)}
                  />
                </Grid.Col>
              </Grid>
              <Grid mb="md">
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Text size="sm" fw={500}>
                    Page
                  </Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <Select
                    data={pageOptions}
                    value={story.pageId?.toString() || ''}
                    onChange={(value) =>
                      handleStateChange({ target: { name: 'pageId', value: value ? parseInt(value) : null } })
                    }
                    placeholder="Select a page"
                    searchable
                  />
                  <Text size="xs" c="dimmed" mt="xs">
                    The page where this story will be displayed.
                  </Text>
                </Grid.Col>
              </Grid>
              <Grid mb="md">
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Text size="sm" fw={500}>
                    Tags
                  </Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <TagsSelect model={story} handleChange={handleStateChange} setUserError={setUserError} />
                </Grid.Col>
              </Grid>
              <Grid mb="md">
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Text size="sm" fw={500}>
                    Slides ({slides.length})
                  </Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <StorySlideList
                    slides={slides}
                    onEdit={(index) => setEditingSlideIndex(index)}
                    onDelete={deleteSlide}
                    onReorder={setSlides}
                  />
                  <Button mt="sm" variant="light" onClick={addSlide} disabled={slides.length >= 20}>
                    <IconPlus size={14} /> Add Slide
                  </Button>
                  {slides.length >= 20 && (
                    <Text size="xs" c="dimmed" mt="xs">
                      Maximum of 20 slides per story.
                    </Text>
                  )}
                </Grid.Col>
              </Grid>
            </Card.Section>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="display" pt={0}>
          <Card withBorder>
            <Card.Section inheritPadding py="sm">
              <Grid mb="md">
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Text size="sm" fw={500}>
                    Trigger
                  </Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <Select
                    data={triggerOptions}
                    onChange={(value) => handleStateChange({ target: { name: 'trigger', value } })}
                    value={story.trigger}
                  />
                </Grid.Col>
              </Grid>
              {story.trigger === 'delaySeconds' && (
                <Grid mb="md">
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <Text size="sm" fw={500}>
                      Show after seconds
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 8 }}>
                    <NumberInput
                      name="delaySeconds"
                      min={0}
                      step={1}
                      value={story.delaySeconds || ''}
                      placeholder="e.g. 5"
                      onChange={(value) => handleStateChange({ target: { name: 'delaySeconds', value } })}
                    />
                  </Grid.Col>
                </Grid>
              )}
              {story.trigger === 'scrollPercentage' && (
                <Grid mb="md">
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <Text size="sm" fw={500}>
                      Show after % scroll
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 8 }}>
                    <NumberInput
                      name="scrollPercentage"
                      min={0}
                      max={100}
                      step={1}
                      value={story.scrollPercentage || ''}
                      placeholder="e.g. 20"
                      onChange={(value) => handleStateChange({ target: { name: 'scrollPercentage', value } })}
                    />
                  </Grid.Col>
                </Grid>
              )}
              {story.trigger === 'leaveIntent' && (
                <Grid mb="md">
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <Text size="sm" fw={500}>
                      Mobile Back button
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 8 }}>
                    <Checkbox
                      label="Also trigger on mobile Back button"
                      checked={!!story.leaveIntentBackButton}
                      onChange={(e) =>
                        handleStateChange({
                          target: { name: 'leaveIntentBackButton', value: e.currentTarget.checked },
                        })
                      }
                    />
                    <Text size="xs" c="dimmed" mt="xs">
                      Leave intent is a mouse movement, so it never fires on a phone. This fallback shows the story on
                      the first Back press instead. It only works on the page the visitor landed on, only after they
                      have interacted with that page, and a second Back press leaves the site. It does not work in
                      in-app browsers such as Instagram or TikTok, nor with the iOS swipe-back gesture.
                    </Text>
                  </Grid.Col>
                </Grid>
              )}
              <URLFilterConfig
                labelCol={{ base: 12, sm: 4 }}
                selectorCol={{ base: 12, sm: 8 }}
                modelInstance={story}
                modelName="story"
                handleChange={handleStateChange}
              />
              <Grid mb="md">
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Text size="sm" fw={500}>
                    Locales to display
                  </Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <MultiSelect
                    data={localesOptions}
                    onChange={(value) => handleStateChange({ target: { name: 'storeLocales', value } })}
                    value={story.storeLocales || []}
                    placeholder="Type to search for locale..."
                  />
                  <Text size="xs" c="dimmed" mt="xs">
                    Restrict display to specific locales. If left empty, it will display in all locales.
                  </Text>
                </Grid.Col>
              </Grid>
            </Card.Section>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="audience" pt={0}>
          <Card withBorder>
            <Card.Section inheritPadding py="sm">
              <SegmentSelector
                title="Behavioral segment"
                description="This determines who will see your story based on aggregated behavior."
                segmentId={story.segmentId}
                onSelect={(segment) => handleStateChange({ target: { name: 'segmentId', value: segment?.id } })}
                campaignScope="marketing"
                scope="behavior"
                forType="banner"
                sizeTitle={4}
                sizeBody={8}
              />
              <Grid mb="md">
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Text size="sm" fw={500}>
                    Action
                  </Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <SegmentedControl
                    value={String(story.inverseSegmentSelection)}
                    onChange={(value) =>
                      handleStateChange({ target: { name: 'inverseSegmentSelection', value: value === 'true' } })
                    }
                    data={[
                      { label: 'Show', value: 'false' },
                      { label: 'Hide', value: 'true' },
                    ]}
                  />
                  <Text size="sm" c="dimmed">
                    This controls whether the story will be shown or hidden for members of the chosen segment.
                  </Text>
                </Grid.Col>
              </Grid>
              <hr />
              <Grid mb="md">
                <Grid.Col span={{ base: 12, sm: 4 }}></Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <SegmentedControl
                    value={story.segmentRelationship ?? ''}
                    onChange={(value) => handleStateChange({ target: { name: 'segmentRelationship', value } })}
                    data={[
                      { label: 'And', value: 'and' },
                      { label: 'Or', value: 'or' },
                    ]}
                  />
                </Grid.Col>
              </Grid>
              <hr />
              <SegmentSelector
                title="Profile filter"
                description="This determines who will see your story based on their current attributes. This filter is applied in real-time."
                segmentId={story.realtimeSegmentId}
                onSelect={(segment) =>
                  handleStateChange({ target: { name: 'realtimeSegmentId', value: segment?.id ?? null } })
                }
                campaignScope="marketing"
                scope="profileFilter"
                forType="banner"
                sizeTitle={4}
                sizeBody={8}
                optional={true}
              />
              <hr />
              <Grid mb="md">
                <Grid.Col span={{ base: 12, sm: 4 }} />
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <SegmentedControl
                    value={story.type ?? ''}
                    onChange={(value) => handleStateChange({ target: { name: 'type', value } })}
                    data={storyTypeOptions}
                  />
                </Grid.Col>
              </Grid>
              {reentryConfig}
              <Grid mb="md">
                <Grid.Col span={{ base: 12, sm: 4 }} mb="md">
                  <Text size="sm" fw={500}>
                    Show when
                  </Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <DatePickerControl
                        prepend="from"
                        size="sm"
                        showTimeSelect
                        placeholder="Any date"
                        value={story.startTime}
                        onChange={(value) => handleStateChange({ target: { name: 'startTime', value } })}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <DatePickerControl
                        prepend="to"
                        append="local time"
                        size="sm"
                        showTimeSelect
                        placeholder="Any date"
                        value={story.endTime}
                        onChange={(value) => handleStateChange({ target: { name: 'endTime', value } })}
                      />
                    </Grid.Col>
                  </Grid>
                </Grid.Col>
              </Grid>
            </Card.Section>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="settings" pt={0}>
          <Card withBorder>
            <Card.Section inheritPadding py="sm">
              <Grid mb="md">
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Text size="sm" fw={500}>
                    Auto-play duration (seconds)
                  </Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <NumberInput
                    name="autoPlayDurationSeconds"
                    min={1}
                    max={60}
                    step={1}
                    value={story.autoPlayDurationSeconds}
                    onChange={(value) => handleStateChange({ target: { name: 'autoPlayDurationSeconds', value } })}
                  />
                  <Text size="xs" c="dimmed" mt="xs">
                    Default duration for each slide before auto-advancing to the next. Individual slides can override
                    this.
                  </Text>
                </Grid.Col>
              </Grid>
              <Grid mb="md">
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Text size="sm" fw={500}>
                    End behavior
                  </Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <SegmentedControl
                    value={story.endBehavior ?? ''}
                    onChange={(value) => handleStateChange({ target: { name: 'endBehavior', value } })}
                    data={endBehaviorOptions}
                  />
                  <Text size="xs" c="dimmed" mt="xs">
                    <strong>Dismiss</strong> - story closes after the last slide. <br />
                    <strong>Loop</strong> - story restarts from the first slide. <br />
                    <strong>Stay on last</strong> - story pauses on the last slide until dismissed.
                  </Text>
                </Grid.Col>
              </Grid>
              <Grid mb="md">
                <Grid.Col span={{ base: 12, sm: 4 }}>
                  <Text size="sm" fw={500}>
                    Progress indicator color
                  </Text>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 8 }}>
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <ColorInput
                        label="Active color"
                        format="hex"
                        value={story.progressIndicatorColor}
                        onChange={(value) => handleStateChange({ target: { name: 'progressIndicatorColor', value } })}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <ColorInput
                        label="Inactive color"
                        format="hex"
                        value={story.progressIndicatorInactiveColor}
                        onChange={(value) =>
                          handleStateChange({ target: { name: 'progressIndicatorInactiveColor', value } })
                        }
                      />
                    </Grid.Col>
                  </Grid>
                </Grid.Col>
              </Grid>
            </Card.Section>
          </Card>
        </Tabs.Panel>
      </Tabs>

      <Group justify="flex-end" gap="xs" py="md">
        {story.id || tabs.indexOf(storyTab) === 0 ? (
          <Button variant="default" onClick={() => cancel()}>
            <IconChevronLeft /> Back
          </Button>
        ) : (
          <Button variant="default" onClick={() => setTab(tabs[tabs.indexOf(storyTab) - 1])}>
            <IconChevronLeft /> Back
          </Button>
        )}
        {deleteButton}
        {copyButton}
        {story.id || tabs.indexOf(storyTab) === tabs.length - 1 ? saveButton : nextButton}
      </Group>

      <ConfirmationModal
        show={confirmDeleteOpen}
        close={() => setConfirmDeleteOpen(false)}
        confirm={(cb) => deleteStory(cb)}
        message="This action cannot be undone!"
      />

      <StorySlideEditor
        opened={editingSlideIndex !== null}
        slide={editingSlideIndex !== null ? slides[editingSlideIndex] : null}
        mergeTags={mergeTags}
        onSave={saveSlide}
        onClose={() => setEditingSlideIndex(null)}
      />

      {userError && (
        <div style={{ textAlign: 'right' }}>
          <ErrorMessage message={userError.message} />
        </div>
      )}
    </>
  );
};

export default StoryForm;
